/**
 * Lumière — core storefront interactions
 * Cart AJAX API (add/update/get), drawer, quick-add, variant selection,
 * sticky add-to-cart, product gallery, accordions.
 * No frameworks — Shopify AJAX Cart API + fetch.
 */
(function () {
  "use strict";

  var routes = {
    cartAdd: "/cart/add.js",
    cartChange: "/cart/change.js",
    cartClear: "/cart/clear.js",
    cartGet: "/cart.js",
  };

  /* ==================== 2 + 1 offer ====================
     Buy 2, get 1 free: for every FREE_EVERY units in the cart, the cheapest
     unit is free (so 6 units → 2 free). Display only — the Worker recomputes
     the identical rule server-side, because anything decided here is just a
     number the shopper's browser handed us. */
  var Offer = {
    FREE_EVERY: 3,

    unitPrices: function (cart) {
      var prices = [];
      cart.items.forEach(function (item) {
        for (var i = 0; i < item.quantity; i++) prices.push(item.final_price);
      });
      return prices.sort(function (a, b) { return a - b; });
    },

    discount: function (cart) {
      var prices = Offer.unitPrices(cart);
      var free = Math.floor(prices.length / Offer.FREE_EVERY);
      return prices.slice(0, free).reduce(function (sum, price) { return sum + price; }, 0);
    },
  };

  /* ==================== Cart Drawer ==================== */
  var Cart = {
    drawer: null,
    overlay: null,

    init: function () {
      this.drawer = document.querySelector("[data-cart-drawer]");
      this.overlay = document.querySelector("[data-cart-overlay]");
      if (!this.drawer) return;

      document.addEventListener("click", this.handleClick.bind(this));
      document.addEventListener("submit", this.handleSubmit.bind(this));
      document.addEventListener("keydown", function (e) {
        if (e.key === "Escape") Cart.close();
      });

      /* Liquid renders the totals without the offer discount, and a cached page
         can show a stale count — reconcile both once on load. */
      this.refresh();
    },

    handleClick: function (e) {
      if (e.target.closest("[data-cart-toggle]")) {
        e.preventDefault();
        this.open();
      }
      if (e.target.closest("[data-cart-close]") || e.target.closest("[data-cart-overlay]")) {
        this.close();
      }
      var qtyBtn = e.target.closest("[data-qty-change]");
      if (qtyBtn) {
        e.preventDefault();
        var line = qtyBtn.dataset.line;
        var delta = parseInt(qtyBtn.dataset.qtyChange, 10);
        /* The quantity is rendered in a <span>, so .value is undefined here and
           reading it produced NaN — every +/- silently sent a broken change. */
        var display = document.querySelector('[data-qty-input][data-line="' + line + '"]');
        var current = parseInt(display.textContent.trim(), 10);
        if (!Number.isFinite(current)) return;
        this.updateLine(line, Math.max(0, current + delta));
      }

      var removeBtn = e.target.closest("[data-cart-remove]");
      if (removeBtn) {
        e.preventDefault();
        this.updateLine(removeBtn.dataset.line, 0);
      }
      var quickAdd = e.target.closest("[data-quick-add]");
      if (quickAdd) {
        e.preventDefault();
        this.addItem(quickAdd.dataset.variantId, 1, quickAdd);
      }
    },

    handleSubmit: function (e) {
      var form = e.target.closest('form[data-product-form]');
      if (!form) return;
      e.preventDefault();
      var formData = new FormData(form);
      var submitBtn = form.querySelector('[type="submit"]');

      /* The gift glasses picker sits outside the form, so its value has to ride
         along as a line-item property — otherwise the colour is lost for anyone
         who buys through the cart instead of the product-page COD form. */
      var giftColor = GiftColors.getSelected();
      var properties = giftColor ? { "لون النظارة": giftColor } : null;

      this.addItem(formData.get("id"), formData.get("quantity") || 1, submitBtn, properties);
    },

    addItem: function (variantId, quantity, triggerEl, properties) {
      if (!variantId) return;
      if (triggerEl) {
        triggerEl.dataset.originalText = triggerEl.textContent;
        triggerEl.textContent = triggerEl.dataset.loadingText || "Adding…";
        triggerEl.disabled = true;
      }
      fetch(routes.cartAdd, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ id: variantId, quantity: quantity, properties: properties || undefined }),
      })
        .then(function (res) { return res.json(); })
        .then(function () {
          return Cart.refresh();
        })
        .then(function () {
          Cart.open();
        })
        .catch(function (err) { console.error("[cart] add failed", err); })
        .finally(function () {
          if (triggerEl) {
            triggerEl.textContent = triggerEl.dataset.originalText;
            triggerEl.disabled = false;
          }
        });
    },

    updateLine: function (line, quantity) {
      fetch(routes.cartChange, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ line: line, quantity: quantity }),
      })
        .then(function () { return Cart.refresh(); })
        .catch(function (err) { console.error("[cart] update failed", err); });
    },

    refresh: function () {
      return fetch(routes.cartGet)
        .then(function (res) { return res.json(); })
        .then(function (cart) {
          Cart.render(cart);
          Cart.updateCount(cart.item_count);
        });
    },

    updateCount: function (count) {
      document.querySelectorAll("[data-cart-count]").forEach(function (el) {
        el.textContent = count;
        el.hidden = count === 0;
      });
    },

    /* Renders every cart surface at once: the drawer and the cart page both
       expose [data-cart-lines]/[data-cart-footer], so a quantity change on
       either one keeps both in sync. */
    render: function (cart) {
      var bodies = document.querySelectorAll("[data-cart-lines]");
      var footers = document.querySelectorAll("[data-cart-footer]");
      if (!bodies.length) return;

      if (cart.item_count === 0) {
        var empty = '<p class="text-muted" data-cart-empty>' + (window.themeStrings.cartEmpty || "Your cart is empty.") + "</p>";
        bodies.forEach(function (body) { body.innerHTML = empty; });
        footers.forEach(function (footer) { footer.hidden = true; });
      } else {
        var html = cart.items
          .map(function (item, i) {
            /* Single-variant products come back with a null variant_title, which
               concatenated straight into the markup as the text "null". */
            var variantLine = item.variant_title
              ? '<p class="text-muted">' + item.variant_title + "</p>"
              : "";
            return (
              '<div class="cart-line" data-line="' + (i + 1) + '">' +
              '<img src="' + item.image + '" alt="' + item.product_title + '" width="76" height="96" loading="lazy">' +
              '<div class="cart-line__details">' +
              "<p>" + item.product_title + "</p>" +
              variantLine +
              '<div class="cart-line__qty">' +
              '<button type="button" data-qty-change="-1" data-line="' + (i + 1) + '" aria-label="Decrease quantity">−</button>' +
              '<span data-qty-input data-line="' + (i + 1) + '">' + item.quantity + "</span>" +
              '<button type="button" data-qty-change="1" data-line="' + (i + 1) + '" aria-label="Increase quantity">+</button>' +
              "</div>" +
              '<button type="button" class="cart-line__remove" data-cart-remove data-line="' + (i + 1) + '">إزالة</button>' +
              "</div>" +
              '<div class="cart-line__price">' + Cart.formatMoney(item.final_line_price) + "</div>" +
              "</div>"
            );
          })
          .join("");
        bodies.forEach(function (body) { body.innerHTML = html; });
        footers.forEach(function (footer) { footer.hidden = false; });
      }

      Cart.updateTotals(cart);
      Cart.updateFreeShipping(cart.total_price);
    },

    updateTotals: function (cart) {
      var discount = Offer.discount(cart);
      document.querySelectorAll("[data-cart-subtotal]").forEach(function (el) {
        el.textContent = Cart.formatMoney(cart.total_price);
      });
      document.querySelectorAll("[data-cart-discount]").forEach(function (el) {
        el.textContent = "−" + Cart.formatMoney(discount);
      });
      document.querySelectorAll("[data-cart-discount-row]").forEach(function (el) {
        el.hidden = discount === 0;
      });
      document.querySelectorAll("[data-cart-total]").forEach(function (el) {
        el.textContent = Cart.formatMoney(cart.total_price - discount);
      });
    },

    updateFreeShipping: function (totalCents) {
      var bar = document.querySelector("[data-free-shipping-bar]");
      if (!bar) return;
      var threshold = parseInt(bar.dataset.threshold, 10) * 100;
      var remaining = Math.max(0, threshold - totalCents);
      var pct = Math.min(100, (totalCents / threshold) * 100);
      var fill = bar.querySelector(".free-shipping-bar__fill");
      var label = bar.querySelector("[data-free-shipping-label]");
      if (fill) fill.style.width = pct + "%";
      if (label) {
        label.textContent =
          remaining === 0
            ? window.themeStrings.freeShippingUnlocked || "You've unlocked free shipping!"
            : (window.themeStrings.freeShippingProgress || "Add {amount} more for free shipping").replace(
                "{amount}",
                Cart.formatMoney(remaining)
              );
      }
    },

    formatMoney: function (cents) {
      return (cents / 100).toLocaleString(document.documentElement.lang || "en", {
        style: "currency",
        currency: window.Shopify && window.Shopify.currency ? window.Shopify.currency.active : "USD",
      });
    },

    open: function () {
      this.drawer.classList.add("is-open");
      this.overlay.classList.add("is-open");
      this.drawer.setAttribute("aria-hidden", "false");
      document.body.style.overflow = "hidden";
    },

    close: function () {
      if (!this.drawer) return;
      this.drawer.classList.remove("is-open");
      this.overlay.classList.remove("is-open");
      this.drawer.setAttribute("aria-hidden", "true");
      document.body.style.overflow = "";
    },
  };

  /* ==================== Variant Picker ==================== */
  var VariantPicker = {
    init: function () {
      document.querySelectorAll("[data-variant-picker]").forEach(function (picker) {
        picker.addEventListener("click", function (e) {
          var btn = e.target.closest(".variant-swatch");
          if (!btn || btn.disabled) return;
          picker.querySelectorAll(".variant-swatch").forEach(function (b) {
            b.classList.remove("is-selected");
          });
          btn.classList.add("is-selected");
          VariantPicker.updateSelection(picker);
        });
      });
    },
    updateSelection: function (picker) {
      var form = picker.closest("[data-product-form]");
      if (!form) return;
      var selectedOptions = [];
      form.querySelectorAll("[data-variant-picker]").forEach(function (p) {
        var selected = p.querySelector(".variant-swatch.is-selected");
        if (selected) selectedOptions.push(selected.dataset.value);
      });
      var variants = JSON.parse(form.dataset.variants || "[]");
      var match = variants.find(function (v) {
        return v.options.every(function (opt, i) { return opt === selectedOptions[i]; });
      });
      var idInput = form.querySelector('input[name="id"]');
      var priceEl = document.querySelector("[data-product-price]");
      var atcBtn = form.querySelector('[type="submit"]');
      if (match) {
        idInput.value = match.id;
        if (priceEl) priceEl.textContent = Cart.formatMoney(match.price);
        if (atcBtn) {
          atcBtn.disabled = !match.available;
          atcBtn.textContent = match.available
            ? atcBtn.dataset.addText || "Add to Cart"
            : atcBtn.dataset.soldOutText || "Sold Out";
        }
        var quickOrderForm = document.querySelector("[data-quick-order]");
        if (quickOrderForm) quickOrderForm.dataset.variantId = match.id;

        /* Swap the main photo to whichever one this variant was tagged with
           (e.g. a colour), matched by media id against the gallery thumbs —
           reusing their already-sized URLs instead of the unsized one Liquid's
           `| json` puts on variant.featured_image. */
        if (match.featured_image) {
          var gallery = document.querySelector("[data-gallery]");
          var matchingThumb =
            gallery && gallery.querySelector('[data-gallery-thumb][data-media-id="' + match.featured_image.id + '"]');
          if (matchingThumb) Gallery.selectThumb(gallery, matchingThumb);
        }
      }
    },
  };

  /* ==================== Gift glasses color picker ==================== */
  var GiftColors = {
    init: function () {
      document.querySelectorAll("[data-gift-colors]").forEach(function (picker) {
        picker.addEventListener("click", function (e) {
          var btn = e.target.closest(".gift-swatch");
          if (!btn) return;
          picker.querySelectorAll(".gift-swatch").forEach(function (b) {
            b.classList.remove("is-selected");
          });
          btn.classList.add("is-selected");
        });
      });
    },
    getSelected: function () {
      var selected = document.querySelector("[data-gift-colors] .gift-swatch.is-selected");
      return selected ? selected.dataset.value : null;
    },
  };

  /* ==================== Product Gallery ==================== */
  var Gallery = {
    init: function () {
      document.querySelectorAll("[data-gallery]").forEach(function (gallery) {
        var main = gallery.querySelector("[data-gallery-main]");
        gallery.querySelectorAll("[data-gallery-thumb]").forEach(function (thumb) {
          thumb.addEventListener("click", function () {
            Gallery.selectThumb(gallery, thumb);
          });
        });
        if (main) {
          main.addEventListener("click", function () {
            main.classList.toggle("is-zoomed");
          });
        }
      });
    },

    selectThumb: function (gallery, thumb) {
      gallery.querySelectorAll("[data-gallery-thumb]").forEach(function (t) {
        t.classList.remove("is-active");
      });
      thumb.classList.add("is-active");
      Gallery.setMainImage(gallery, thumb.dataset.fullSrc);
    },

    setMainImage: function (gallery, src) {
      var main = gallery.querySelector("[data-gallery-main]");
      if (!main) return;

      // The featured media can be a <video> (when a product's first media is a
      // video) — pause and remove it before swapping in the new image,
      // otherwise the video keeps playing underneath.
      var existingVideo = main.querySelector("video");
      if (existingVideo) {
        existingVideo.pause();
        existingVideo.remove();
      }

      var img = main.querySelector("img");
      if (!img) {
        img = document.createElement("img");
        img.alt = "";
        main.appendChild(img);
      }
      img.src = src;
    },
  };

  /* ==================== Lazy video (product-card grid) ====================
     Home/collection cards can each carry an autoplaying video. Loading and
     playing all of them the instant the page paints is what was making the
     storefront take 5-10s to feel usable on a slow connection — every card
     below the fold was pulling its full video file before the shopper had
     scrolled anywhere near it. This defers each one until it's about to
     enter the viewport, and pauses it again once it scrolls away so an idle
     tab isn't still streaming six looping videos in the background. */
  var LazyVideo = {
    init: function () {
      var videos = document.querySelectorAll("[data-lazy-video]");
      if (!videos.length) return;

      if (!("IntersectionObserver" in window)) {
        // No IO support — fall back to loading everything up front rather
        // than showing a permanently-blank poster.
        videos.forEach(LazyVideo.load);
        return;
      }

      var observer = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            var video = entry.target;
            if (entry.isIntersecting) {
              if (!video.dataset.loaded) LazyVideo.load(video);
              video.play().catch(function () { /* autoplay can be blocked; poster stays visible */ });
            } else if (video.dataset.loaded) {
              video.pause();
            }
          });
        },
        { rootMargin: "200px 0px" } // start fetching slightly before the card is on screen
      );

      videos.forEach(function (video) {
        observer.observe(video);
      });
    },

    load: function (video) {
      var src = video.dataset.src;
      if (!src) return;
      var source = document.createElement("source");
      source.src = src;
      source.type = "video/mp4";
      video.appendChild(source);
      video.load();
      video.dataset.loaded = "true";
    },
  };

  /* ==================== Sticky Add To Cart ==================== */
  var StickyATC = {
    init: function () {
      var sticky = document.querySelector("[data-sticky-atc]");
      var trigger = document.querySelector("[data-atc-trigger]");
      if (!sticky || !trigger) return;
      var observer = new IntersectionObserver(
        function (entries) {
          sticky.classList.toggle("is-visible", !entries[0].isIntersecting);
        },
        { threshold: 0 }
      );
      observer.observe(trigger);
    },
  };

  /* ==================== Quick COD Order ====================
     Collects name/phone/address, adds the item to cart with those
     as line-item properties + a cart note, then jumps straight to
     Shopify's real checkout (skips the cart page). No admin
     credentials are ever exposed client-side — this only talks to
     Shopify's public cart endpoints. */
  var QUICK_ORDER_ENDPOINT = "https://orel-checkout.dadasnapwahd.workers.dev";

  /* Attribution for the Worker's server-side Purchase event. The COD form never
     reaches Shopify's checkout, so the server sees none of the browser's
     cookies — forwarding _fbp/_fbc is what lets Meta tie a sale back to the ad
     click that caused it. */
  var Tracking = {
    FBCLID_KEY: "orel_fbclid",

    init: function () {
      /* Persist the click id on landing: the visitor usually scrolls and fills
         the form on a later view where the query string is long gone. */
      try {
        var fbclid = new URLSearchParams(window.location.search).get("fbclid");
        if (fbclid) window.sessionStorage.setItem(this.FBCLID_KEY, fbclid);
      } catch (e) {
        /* Some in-app browsers block session storage. Ordering must still work. */
      }
    },

    readCookie: function (name) {
      var parts = document.cookie.split("; ");
      for (var i = 0; i < parts.length; i++) {
        if (parts[i].indexOf(name + "=") === 0) return parts[i].slice(name.length + 1);
      }
      return undefined;
    },

    params: function () {
      var fbc = this.readCookie("_fbc");

      /* _fbc only exists once the pixel has run with an fbclid present, which
         in-app browsers routinely prevent. Rebuild it from the stored click id. */
      if (!fbc) {
        var fbclid = null;
        try {
          fbclid =
            new URLSearchParams(window.location.search).get("fbclid") ||
            window.sessionStorage.getItem(this.FBCLID_KEY);
        } catch (e) {
          /* Storage blocked — fall through with no fbc. */
        }
        if (fbclid) fbc = "fb.1." + Date.now() + "." + fbclid;
      }

      return {
        fbp: this.readCookie("_fbp"),
        fbc: fbc,
        eventSourceUrl: window.location.href,
      };
    },

    track: function (event, data, eventId) {
      if (typeof window.fbq !== "function") return;
      if (eventId) {
        window.fbq("track", event, data, { eventID: eventId });
      } else {
        window.fbq("track", event, data);
      }
    },
  };

  var QuickOrder = {
    checkoutTracked: false,

    init: function () {
      var form = document.querySelector("[data-quick-order]");
      if (!form) return;
      form.addEventListener("submit", this.handleSubmit.bind(this));

      /* Fire on first engagement, not on submit. Purchases are far too rare to
         get an ad set out of Meta's learning phase (~50 events/week), so this is
         the event delivery is optimised against — it needs the volume of
         everyone who starts the form, matching the landing page's behaviour. */
      form.addEventListener("focusin", this.trackCheckoutStart.bind(this), { once: true });
    },

    trackCheckoutStart: function () {
      if (this.checkoutTracked) return;
      this.checkoutTracked = true;

      var form = document.querySelector("[data-quick-order]");
      var variantId = form ? String(form.dataset.variantId).replace(/\D/g, "") : "";

      Tracking.track("InitiateCheckout", {
        content_ids: [variantId],
        content_type: "product",
        currency: "MAD",
        num_items: 1,
      });
    },
    handleSubmit: function (e) {
      e.preventDefault();
      var form = e.target;
      if (form.dataset.submitting === "true") return;
      form.dataset.submitting = "true";
      var submitBtn = form.querySelector('[type="submit"]');
      var variantIdGid = form.dataset.variantId;
      var variantId = String(variantIdGid).replace(/\D/g, "");
      var name = form.querySelector('[name="name"]').value;
      var phone = form.querySelector('[name="phone"]').value;
      var city = form.querySelector('[name="city"]').value;
      var address = form.querySelector('[name="address"]').value;

      submitBtn.disabled = true;
      submitBtn.textContent = submitBtn.dataset.loadingText || "…";

      /* Normally already fired on focus; this covers autofill and any path that
         reaches submit without focusing a field. Deduplicated internally. */
      this.trackCheckoutStart();

      var fbParams = Tracking.params();

      fetch(QUICK_ORDER_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          variantId: variantId,
          quantity: 1,
          name: name,
          phone: phone,
          city: city,
          address: address,
          glassesColor: GiftColors.getSelected(),
          fbp: fbParams.fbp,
          fbc: fbParams.fbc,
          eventSourceUrl: fbParams.eventSourceUrl,
        }),
      })
        .then(function (res) { return res.json().then(function (data) { return { ok: res.ok, data: data }; }); })
        .then(function (result) {
          if (!result.ok || !result.data.success) {
            throw new Error((result.data && result.data.error) || "order failed");
          }

          /* Same event_id the Worker sends server-side, so Meta counts one
             purchase whether the pixel is blocked, the server call fails, or
             both land. */
          Tracking.track(
            "Purchase",
            {
              content_ids: [variantId],
              content_type: "product",
              currency: "MAD",
              num_items: 1,
              order_id: result.data.orderName,
              value: result.data.total,
            },
            "purchase_" + result.data.orderName
          );

          form.innerHTML =
            '<p style="color:var(--color-accent);font-size:1rem;">✔ تم تسجيل الطلب ديالك بنجاح! رقم الطلب: ' +
            result.data.orderName +
            "<br>غادي نتصلو بيك من هاتفك باش نأكدو الطلب.</p>";
        })
        .catch(function (err) {
          console.error("[quick-order] failed", err);
          form.dataset.submitting = "false";
          submitBtn.disabled = false;
          submitBtn.textContent = submitBtn.dataset.originalText || "Order Now";
          alert("وقع مشكل، عافاك عاود المحاولة أو تواصل معنا مباشرة.");
        });
    },
  };

  /* ==================== Cart COD Order ====================
     The whole-cart counterpart of QuickOrder. There is no Shopify checkout on
     this store, so this is the only exit from the cart: it posts every line to
     the Worker, which creates the order and applies the 2+1 discount itself. */
  var CartOrder = {
    checkoutTracked: false,

    init: function () {
      if (!document.querySelector("[data-cod-order]")) return;
      document.addEventListener("submit", this.handleSubmit.bind(this));
      document.addEventListener("focusin", this.trackCheckoutStart.bind(this));
    },

    /* Same reasoning as QuickOrder: purchases are too rare to train an ad set,
       so the optimisation event fires when someone starts filling the form. */
    trackCheckoutStart: function (e) {
      if (this.checkoutTracked) return;
      if (e && !e.target.closest("[data-cod-order]")) return;
      this.checkoutTracked = true;

      fetch(routes.cartGet)
        .then(function (res) { return res.json(); })
        .then(function (cart) {
          Tracking.track("InitiateCheckout", {
            content_ids: cart.items.map(function (item) { return String(item.variant_id); }),
            content_type: "product",
            currency: "MAD",
            num_items: cart.item_count,
          });
        })
        .catch(function () { /* tracking must never block the order */ });
    },

    handleSubmit: function (e) {
      var form = e.target.closest("[data-cod-order]");
      if (!form) return;
      e.preventDefault();
      if (form.dataset.submitting === "true") return;
      form.dataset.submitting = "true";

      var submitBtn = form.querySelector('[type="submit"]');
      var field = function (name) { return form.querySelector('[name="' + name + '"]').value.trim(); };
      var fbParams = Tracking.params();
      var payload = {
        name: field("name"),
        phone: field("phone"),
        city: field("city"),
        address: field("address"),
        fbp: fbParams.fbp,
        fbc: fbParams.fbc,
        eventSourceUrl: fbParams.eventSourceUrl,
      };

      submitBtn.disabled = true;
      submitBtn.textContent = submitBtn.dataset.loadingText || "…";

      /* Covers autofill and any path that reaches submit without a focus event. */
      this.trackCheckoutStart();

      // Read the cart at submit time rather than trusting whatever was rendered.
      fetch(routes.cartGet)
        .then(function (res) { return res.json(); })
        .then(function (cart) {
          if (!cart.items.length) throw new Error("cart is empty");
          payload.items = cart.items.map(function (item) {
            return {
              variantId: String(item.variant_id),
              quantity: item.quantity,
              properties: item.properties || null,
            };
          });

          return fetch(QUICK_ORDER_ENDPOINT, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(payload),
          });
        })
        .then(function (res) { return res.json().then(function (data) { return { ok: res.ok, data: data }; }); })
        .then(function (result) {
          if (!result.ok || !result.data.success) {
            throw new Error((result.data && result.data.error) || "order failed");
          }

          Tracking.track(
            "Purchase",
            {
              content_ids: payload.items.map(function (item) { return item.variantId; }),
              content_type: "product",
              currency: "MAD",
              num_items: payload.items.reduce(function (n, item) { return n + item.quantity; }, 0),
              order_id: result.data.orderName,
              value: result.data.total,
            },
            "purchase_" + result.data.orderName
          );

          // The order is in Shopify now; the cart is leftover state.
          return fetch(routes.cartClear, { method: "POST" })
            .catch(function () { /* an uncleared cart must not fail the order */ })
            .then(function () { return result.data.orderName; });
        })
        .then(function (orderName) {
          /* Deliberately not Cart.refresh() — re-rendering the footer would wipe
             out the confirmation the shopper is reading. */
          Cart.updateCount(0);
          document.querySelectorAll("[data-cart-lines]").forEach(function (body) {
            body.innerHTML = "";
          });
          document.querySelectorAll("[data-cart-footer]").forEach(function (footer) {
            footer.hidden = false;
            footer.innerHTML =
              '<p style="color:var(--color-accent);font-size:1rem;">✔ تم تسجيل الطلب ديالك بنجاح! رقم الطلب: ' +
              orderName +
              "<br>غادي نتصلو بيك فهاتفك باش نأكدو الطلب.</p>";
          });
        })
        .catch(function (err) {
          console.error("[cod-order] failed", err);
          form.dataset.submitting = "false";
          submitBtn.disabled = false;
          submitBtn.textContent = submitBtn.dataset.originalText || "تأكيد الطلب";
          alert("وقع مشكل، عافاك عاود المحاولة أو تواصل معنا مباشرة.");
        });
    },
  };

  /* ==================== Accordion ==================== */
  /* ==================== Mobile menu ==================== */
  var MobileMenu = {
    init: function () {
      var toggle = document.querySelector("[data-mobile-menu-toggle]");
      var menu = document.querySelector("[data-mobile-menu]");
      if (!toggle || !menu) return;
      toggle.addEventListener("click", function () {
        var isOpen = menu.classList.toggle("is-open");
        toggle.setAttribute("aria-expanded", isOpen ? "true" : "false");
      });
      menu.querySelectorAll("a").forEach(function (link) {
        link.addEventListener("click", function () {
          menu.classList.remove("is-open");
          toggle.setAttribute("aria-expanded", "false");
        });
      });
    },
  };

  var Accordion = {
    init: function () {
      document.querySelectorAll(".accordion__trigger").forEach(function (trigger) {
        trigger.addEventListener("click", function () {
          var item = trigger.closest(".accordion__item");
          var panel = item.querySelector(".accordion__panel");
          var isOpen = item.classList.contains("is-open");
          item.classList.toggle("is-open", !isOpen);
          panel.style.maxHeight = isOpen ? null : panel.scrollHeight + "px";
        });
      });
    },
  };

  window.themeStrings = window.themeStrings || {};

  document.addEventListener("DOMContentLoaded", function () {
    Tracking.init();
    Cart.init();
    VariantPicker.init();
    GiftColors.init();
    Gallery.init();
    LazyVideo.init();
    StickyATC.init();
    QuickOrder.init();
    CartOrder.init();
    MobileMenu.init();
    Accordion.init();
  });
})();
